import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { Button } from '@treezor/design-system';

import { NavigationTabs } from '@/ui-kit/navigation-tabs/NavigationTabs';

import { useAppSelector } from '@/hooks/redux-helpers';

import { useRoutepaths } from '@/router/hooks/useRoutePaths';
import { removeAllProfile, removeProfile } from '@/store/actions/profile.actions';

import { ITab } from '@/store/models/profile.model';

import { IUser } from '@/features/user/store/user.model';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

export function ViewedProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const PathRoute = useRoutepaths();
  const { pathname } = useLocation();

  const tabs = useAppSelector<ITab[] | undefined>(
    (state) =>
      state.profile.profiles.state
        ?.map((profileState) => profileState.state?.user.state as IUser)
        .map((user) => ({
          id: `${user.userId}`,
          label: DashboardHelpers.getUserName(user) || t('pages.currentProfiles.unknownUser', { userId: user.userId }),
          path: DashboardHelpers.generateProfilePath(user.userId),
          onCloseTab: () => {
            dispatch(removeProfile(user.userId));
            handleNavigateProfile(`${user.userId}`);
          },
        })),
    {
      devModeChecks: { stabilityCheck: 'never' },
    },
  );

  const handleNavigateProfile = (tabId: string) => {
    const currentTabIndex = tabs.findIndex(({ id }) => pathname.includes(id)) || 0;
    const indexIdToRemove = tabs?.findIndex(({ id }) => id === tabId) || 0;
    if (indexIdToRemove !== currentTabIndex) return;
    const nextIdToNavigate = indexIdToRemove === 0 ? tabs?.[1]?.id : tabs?.[indexIdToRemove - 1]?.id;

    if (nextIdToNavigate) {
      navigate(DashboardHelpers.generateProfilePath(nextIdToNavigate));
    } else {
      navigate(generatePath(PathRoute.app.root, { '*': PathRoute.app.users.main }));
    }
  };

  const clearViewedProfiles = useCallback(() => {
    dispatch(removeAllProfile());
    navigate(generatePath(PathRoute.app.root, { '*': PathRoute.app.users.main }));
  }, [PathRoute.app.users.main, PathRoute.app.root, dispatch, navigate]);

  return (
    <div className="flex gap-4 w-full">
      <NavigationTabs items={tabs} size="s" className="w-full px-3 flex-grow max-w-[80%]" scrollStepRatio={0.6} />
      {!!tabs?.length && (
        <Button
          size="s"
          variant="ghost"
          onClick={clearViewedProfiles}
          data-testid={`btn-clear-all-viewed-profile`}
          className="h-fit mt-2 flex-shrink-0 ml-auto mr-4"
        >
          {t('pages.currentProfiles.clearViewedProfiles')}
        </Button>
      )}
    </div>
  );
}
